// audience-list.tsx — the Audiences tab's MANAGEMENT list.
//
// The audience equivalent of `template-list.tsx`: a clean list of audience
// boards (search, contact count, Edit / Duplicate / Delete via the shared
// `ActionsMenu`). Edit delegates to the REAL contacts editor through the
// redesign's nav context (`openBoard`) — this list never re-implements the
// contacts table itself.
//
// Visual language matches the rest of the Templates/Campaigns/Audiences tabs
// (centered `CARD_COLUMN_MAX` column, `.campaigns-rd` CSS-var tokens, `.btn`/
// `.inp` utility classes) so it reads as one system, not a bolted-on legacy
// view.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Pencil, Copy, Trash2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBBoardsThunks, boardsSelectByQuery, AUDIENCE_LIST_QUERY } from '@common-reducers/DBServiceThunks';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';
import { openNewAudienceModal } from '@extension/context/content/components/bt-bulk-dashboard/audiences/bulk-audiences-list';
import ImportCSVOrGoogleSheets from '@extension/context/content/components/bt-bulk-dashboard/audiences/import-csv-or-google-sheets';
import { ActionsMenu, type ActionItem } from '@extension/context/content/components/campaigns-redesign/action-menu';
import { ListSkeleton } from '@extension/context/content/components/campaigns-redesign/primitives';
import { FirstRunEmptyState } from '@extension/context/content/components/campaigns-redesign/empty-state';
import { Board } from '@common-models/board';
import { namespace } from '@common/config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import moment from 'moment';

const CARD_COLUMN_MAX = 1040;

const AudienceRow: React.FC<{ board: Board; count: number }> = ({ board, count }) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const nav = useCampaignsRedesignNav();

  const id = String(board._id ?? '');

  // Whole-row Edit: opening the real contacts editor via the redesign nav —
  // the SAME action the "…" menu's Edit item fires.
  const openEditor = () => nav?.openBoard(id);

  const actionItems: ActionItem[] = [
    {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: Pencil,
      onSelect: openEditor,
    },
    {
      key: 'duplicate',
      label: t('common.duplicate', 'Duplicate'),
      icon: Copy,
      onSelect: () => {
        const { _id: _ignored, ...boardWithoutId } = board;
        dispatch(
          DBBoardsThunks.create({
            ...boardWithoutId,
            name: (board.name ?? t('audiences.unnamed', 'Untitled audience')) + t('campaignsRedesign.copySuffix', ' (copy)'),
          }),
        );
      },
    },
    {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: Trash2,
      danger: true,
      confirm: true,
      onSelect: () => {
        dispatch(DBBoardsThunks.patch({ entity: { ...board, deleted: true } }));
      },
    },
  ];

  // ONE line of text and one graphic, per the house density rule. The name takes
  // the row; the size and the age are marks at the trailing edge.
  //
  // The count is rendered through the shared `.pill` primitive rather than a
  // second grey line, so it reads as an object and carries the row's one
  // saturated mark (the Users glyph). `createdAt` is new on the row but not new
  // data: the list is ALREADY sorted by it (AUDIENCE_LIST_QUERY), so a person
  // was looking at an ordering whose key was invisible.
  // 'MMM D, YYYY' localized the month NAME but hardcoded the US ORDER, so Hebrew
  // read "יולי 9, 2026". 'll' is the locale's OWN short date, which puts the day
  // first where the language does — the same fix already applied to the compose
  // preview's long date.
  const created = board.createdAt
    ? formatInLanguage(moment(board.createdAt), i18n.language, 'll')
    : '';

  return (
    <div
      role="button"
      tabIndex={0}
      className="aud-row"
      onClick={openEditor}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openEditor();
        }
      }}
    >
      <span className="aud-name">
        {board.name || t('audiences.unnamed', 'Untitled audience')}
      </span>

      <span className="pill brand">
        <Users size={12} />
        {count > 0
          ? t('audiences.contactCount', '{{count}} contacts', { count })
          : t('audiences.noContactsYet', 'No contacts yet')}
      </span>

      {created && (
        <span className="aud-meta">
          {t('audiences.createdOn', 'Created {{date}}', { date: created })}
        </span>
      )}

      {/* stopPropagation so opening/using the "…" menu never also triggers the
          row's edit-on-click. */}
      <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
        <ActionsMenu items={actionItems} />
      </div>
    </div>
  );
};

export const AudienceList: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const nav = useCampaignsRedesignNav();

  const boards = useAppSelector((state) =>
    boardsSelectByQuery(state.DBBoardsReducer, { type: 'audience', deleted: { $ne: true } }),
  ) as Board[];

  const taskSum = useAppSelector(getTaskSumByBoardIdRecord);
  // First-load flag: skeleton until the boards collection resolves once, so a
  // cold reload doesn't flash "No audiences yet" before the rows arrive.
  const itemsLoaded = useAppSelector((state) => state.DBBoardsReducer.itemsLoaded);

  useEffect(() => {
    // Load ALL non-deleted audiences — this list renders from boardsSelectByQuery with
    // no load-more, so the server default paginate (10) would hide every audience past
    // the first ten. AUDIENCE_LIST_QUERY carries the high $limit + sort (see its docs).
    dispatch(DBBoardsThunks.find({ type: 'audience', deleted: { $ne: true }, ...AUDIENCE_LIST_QUERY }));
  }, [dispatch]);

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter((board) => (board.name ?? '').toLowerCase().includes(q));
  }, [boards, search]);

  // First-run vs "loaded but filtered to nothing". Only the FIRST hides the
  // toolbar: with zero audiences there is nothing to search, so the search box
  // is noise standing between the user and the one button that matters.
  const isFirstRun = itemsLoaded && boards.length === 0;

  // ── New-Audience import modal: open state as a URL flag (?new=audience) ──
  // FE#377: the "New audience" button navigates to ?tab=audiences&new=audience
  // (nav.newAudience). This list is what actually renders on that surface, so it
  // owns the flag → modal wiring (the legacy ManagementViewAdapter that used to
  // host this effect is never mounted on the redesign path, so its copy was dead
  // code and the button did nothing). We open the SAME shared import dialog the
  // campaign flow uses (openNewAudienceModal + <ImportCSVOrGoogleSheets/>), so
  // there is one import path and no drift. Reload/back restore the open modal;
  // dismissing it clears the flag.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantNewAudience = searchParams.get('new') === 'audience';
  const isImportModalOpen = useAppSelector((state) => state.CampaignReducer.isImportModalOpen);
  const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
  const openedForFlagRef = useRef(false);
  useEffect(() => {
    if (!wantNewAudience) { openedForFlagRef.current = false; return; }
    if (isImportModalOpen) { openedForFlagRef.current = true; return; }
    // Flag set, modal currently closed:
    if (openedForFlagRef.current) {
      // …it was open and the user dismissed it → drop the flag (replace, so the
      // dismissed-modal URL isn't a back-button trap).
      openedForFlagRef.current = false;
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    } else {
      // …not opened yet (fresh navigation / cold reload with the flag) → open it.
      openNewAudienceModal(dispatch, activeBulkView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantNewAudience, isImportModalOpen]);

  return (
    <div style={{ padding: '8px 24px 24px', width: '100%', maxWidth: CARD_COLUMN_MAX, margin: '0 auto', boxSizing: 'border-box' }}>
      {/* header: search + new audience */}
      {!isFirstRun && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search
            size={14}
            style={{ position: 'absolute', insetInlineStart: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-2)', pointerEvents: 'none' }}
          />
          <input
            className="inp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('audiences.searchPlaceholder', 'Search audiences')}
            style={{ paddingInlineStart: 32 }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn primary" onClick={() => nav?.newAudience()} disabled={!nav}>
          <Plus size={14} /> {t('audiences.newAudience', 'New audience')}
        </button>
      </div>
      )}

      {/* list / loading / empty states */}
      {!itemsLoaded && boards.length === 0 ? (
        <ListSkeleton />
      ) : isFirstRun ? (
        <FirstRunEmptyState
          icon={Users}
          headline={t('audiences.emptyHeadline', 'Create your first audience')}
          body={t(
            'audiences.emptyBody',
            'An audience is the list of contacts a campaign sends to. Add contacts once, then reuse the list in every campaign.',
          )}
          ctaLabel={t('audiences.newAudience', 'New audience')}
          onCta={() => nav?.newAudience()}
          ctaDisabled={!nav}
        />
      ) : filtered.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '56px 24px',
            textAlign: 'center',
            color: 'var(--muted)',
          }}
        >
          <Users size={26} style={{ opacity: 0.5 }} />
          <span style={{ fontSize: 13 }}>
            {t('audiences.noResults', 'No audiences match your search')}
          </span>
        </div>
      ) : (
        <div className="aud-list">
          {filtered.map((board) => (
            <AudienceRow key={String(board._id ?? '')} board={board} count={taskSum[String(board._id ?? '')] ?? 0} />
          ))}
        </div>
      )}

      {/* FE#377: the shared import dialog (same one the campaign flow mounts inside
          BulkAudiencesList). Reads isImportModalOpen from Redux and self-manages its
          own open/close; opened via the ?new=audience flag effect above. */}
      <ImportCSVOrGoogleSheets />
    </div>
  );
};

export default AudienceList;
